import { MAX_PLANET_NAME, Planet } from '@apeiron/planet'
import { useAccountContext } from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import { RelicsTokenView } from '@src/components/planet/detail/RelicsTokenView'
import { PlanetExordiumBackground as PlanetDetailCardBackground } from '@src/components/planet/share/planetCard/PlanetExordiumBackground'
import ApeironBookmarkButton from '@src/components/share/apeiron/ApeironBookmarkButton'
import ApeironNameInput from '@src/components/share/apeiron/ApeironNameInput'
import ApeironShareButton from '@src/components/share/apeiron/ApeironShareButton'
import ENV_CONFIG from '@src/config'
import { NftType } from '@src/constants/bookmark'
import { ROUTE } from '@src/constants/route'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import useURL from '@src/hooks/common/router/useURL'
import { getRelicsTokens, isCore } from '@src/util/apeiron/planet'
import { FC, useCallback, useMemo, useState } from 'react'

const Container = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column-reverse;
`

const ShareButton = styled(ApeironShareButton)`
  margin-left: 5px;
`

const InputSectionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  margin: 0px 9px 12px 9px;
  ${props => props.theme.breakpoints.up('res1280')} {
    width: 340px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    align-self: center;
    max-width: 340px;
    width: 100%;
  }
`

const CardContainer = styled(Box)`
  position: relative;
  ${props => props.theme.breakpoints.down('res1280')} {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`

const Background = styled(PlanetDetailCardBackground)`
  width: 317px;
  height: 437px;
`

const IdText = styled(Box)`
  color: #b1b3b3;
  font-size: 16px;
  line-height: 16px;
  margin-bottom: 1px;
`

const AddFavoriteButton = styled(ApeironBookmarkButton)`
  margin-left: 12px;
`

const StyledRelicsTokenView = styled(RelicsTokenView)`
  margin: auto;
  margin-top: 8px;
`

const BasicNameDisplay = styled(Box)`
  background-color: #151615;
  color: #ffffff;
  font-size: 20px;
  height: 28px;
  padding: 2px 10px;
`

export const PlanetDetailCard: FC<Props> = props => {
  const {
    className,
    planet,
    isBookmarked,
    isEditable,
    onBookmarkChange,
    onNameUpdate,
    shouldShowRelic,
    showEnhancedNameComponent = true
  } = props

  const { isLoggedIn } = useAccountContext()

  const [isEditing, setIsEditing] = useState(false)

  const planetTypeContext = usePlanetTypeContext()

  const { buildURL } = useURL()

  const planetTypeData = planet.planetType
    ? planetTypeContext.getPlanetTypeData(planet.planetType)
    : undefined

  const handleOnSave = useCallback(
    (name: string) => {
      onNameUpdate(name)
    },
    [onNameUpdate]
  )

  const shareURL = useMemo(() => {
    return buildURL(
      ENV_CONFIG.WEB_URL.MARKETPLACE,
      ROUTE.MARKETPLACE_PLANET_DETAIL,
      {
        planetId: planet?.planetID
      }
    )
  }, [buildURL, planet?.planetID])

  return (
    <Container className={className}>
      <CardContainer>
        <Background
          coreType={planet.coreType}
          iconURL={planetTypeData?.planetTypeIcon}
          imageURL={planet.image ?? ''}
          name={planet.name}
        />
        {shouldShowRelic && !isCore(planet) && (
          <StyledRelicsTokenView relicsTokens={getRelicsTokens(planet)} />
        )}
      </CardContainer>
      <InputSectionContainer>
        <IdText>#{planet.planetID}</IdText>
        {showEnhancedNameComponent ? (
          <ApeironNameInput
            buttonPlaceholder={
              <>
                {isLoggedIn && (
                  <AddFavoriteButton
                    isBookmarked={isBookmarked}
                    nftType={NftType.Planet}
                    tokenId={planet.planetID}
                    onChange={onBookmarkChange}
                  />
                )}
                <ShareButton url={shareURL} />
              </>
            }
            canEdit={isEditable}
            defaultName={planet.name}
            isEditing={isEditing}
            maxLength={MAX_PLANET_NAME}
            onSave={handleOnSave}
            setIsEditing={setIsEditing}
          />
        ) : (
          <BasicNameDisplay>{planet.name}</BasicNameDisplay>
        )}
      </InputSectionContainer>
    </Container>
  )
}

type Props = {
  className?: string
  isBookmarked: boolean
  isEditable: boolean
  planet: Planet
  shouldShowRelic?: boolean
  onBookmarkChange?: (bookmarked: boolean) => void
  onNameUpdate: (name: string) => void
  showEnhancedNameComponent?: boolean
}
